import { inject, Injectable } from "@angular/core";
import { debounce, first } from "lodash-es";
import { AuthService } from "src/app/services/auth.service";
import { environment } from "src/environments/environment";
import { BootstrapService } from "../modules/bootstrap/bootstrap.service";
import { BoxService } from "./box.service";
import { uuid } from "src/app/support/uuid";
import { Box } from "src/app/modules/bootstrap/rocket-ui/backend/models/box";
import { Nullable } from "src/app/support/nullable";

type OHIFViewerConfig = {
  maxNumberOfWebWorkers: number;
  useNorm16Texture: boolean;
  useSharedArrayBuffer: "TRUE" | "FALSE" | "AUTO";
  dataSourceConfig: {};
};

@Injectable()
export class MedicalImageViewerService {
  private readonly bootstrapService = inject(BootstrapService);
  private readonly boxService = inject(BoxService);
  private readonly authenticationService = inject(AuthService);

  private iframeMap: Record<
    string,
    { iframe: HTMLIFrameElement; resizeObserver: ResizeObserver }
  > = {};

  async install(
    studyUuid: string,
    storageId: string,
    tenantId: string,
    container: HTMLElement
  ): Promise<string> {
    const iframe: HTMLIFrameElement = document.createElement("iframe");

    const members = this.bootstrapService.getAllMembers();
    const rocketMember = members?.find(
      (x) => x.tenant.type === "rocket" && x.tenant.id === tenantId
    );

    if (!rocketMember) {
      return "";
    }

    try {
      const rocketToken = await this.authenticationService.getValidBearerToken(
        rocketMember.member,
        rocketMember.tenant.type,
        true
      );

      const { url, memberToken, configuration } =
        await this.getStandalonViewerConfig({
          studyUuid,
          storageId,
          tenantId,
          memberToken: rocketToken,
          studyIsShared: false
        });

      iframe.allow = `fullscreen; local-network-access *;`;
      iframe.classList.add("rounded-md");
      iframe.src = url.toString();
      iframe.style.position = "absolute";

      const viewerOrigin = new URL(url);

      window.addEventListener(
        "message",
        (event) => {
          if (event.data.type === "ohif-loaded") {
            iframe.contentWindow?.postMessage(
              {
                type: "ohif-config",
                data: JSON.stringify({
                  memberToken,
                  configuration
                })
              },
              viewerOrigin.origin
            );
          }
        },
        {
          once: true
        }
      );
    } catch (error) {
      console.error(error);
      return "";
    }

    updateIframeLinkedPosition(iframe, container);

    document.body.appendChild(iframe);

    const resizeObserver = new ResizeObserver(
      debounce((entries: ResizeObserverEntry[], observer: ResizeObserver) => {
        const { target } = first(entries)!;

        updateIframeLinkedPosition(iframe, target);
      }, 250)
    );

    resizeObserver.observe(container);

    const uuidRef = uuid();
    this.iframeMap[uuidRef] = {
      iframe,
      resizeObserver
    };

    return uuidRef;
  }

  async installNewWindowBlank({
    studyUuid,
    storageId,
    tenantId,
    memberToken
  }: {
    studyUuid: string;
    storageId: string;
    tenantId: string;
    memberToken: string;
  }): Promise<void> {
    const { url, configuration } = await this.getStandalonViewerConfig({
      studyUuid,
      tenantId,
      storageId,
      memberToken: `Bearer ${memberToken}`,
      studyIsShared: false
    });

    const message = btoa(
      JSON.stringify({
        memberToken,
        configuration
      })
    );

    const urlWithMessage = `${url}&message=${message}`;

    window.open(urlWithMessage, "_blank");
  }

  async uninstall(studyUuid: string): Promise<void> {
    const item = this.iframeMap[studyUuid];
    if (!item) {
      return;
    }

    item.iframe.remove();
    item.resizeObserver.disconnect();

    delete this.iframeMap[studyUuid];
  }

  async show(studyUuid: string): Promise<void> {
    const item = this.iframeMap[studyUuid];
    if (!item) {
      return;
    }

    item.iframe.style.zIndex = "";
  }

  async hide(studyUuid: string): Promise<void> {
    const item = this.iframeMap[studyUuid];
    if (!item) {
      return;
    }

    item.iframe.style.zIndex = "-9999";
  }

  async getStandalonViewerConfig({
    studyUuid,
    storageId,
    tenantId,
    memberToken,
    studyIsShared
  }: {
    studyUuid: string;
    storageId: string;
    tenantId: string;
    memberToken: string;
    studyIsShared: boolean;
  }): Promise<{
    url: string;
    memberToken: string;
    configuration: OHIFViewerConfig;
  }> {
    const url = new URL(environment.snoots.viewer.iframeSrc());

    url.searchParams.set("StudyInstanceUIDs", studyUuid);

    const box: Nullable<Box> = this.bootstrapService.getCurrentBox();
    const boxAddress: string | undefined = box?.address;

    const isOnline: boolean = await this.boxService.isOnline();

    return {
      url: url.toString(),
      memberToken,
      configuration: {
        maxNumberOfWebWorkers: window.navigator.hardwareConcurrency,
        useNorm16Texture: true,
        useSharedArrayBuffer: "TRUE",
        dataSourceConfig: getMedicalImageViewerConfig({
          boxAvailable: isOnline,
          storageId,
          tenantId,
          host: boxAddress,
          studyIsShared
        })
      }
    };
  }
}

function updateIframeLinkedPosition(
  iframe: HTMLIFrameElement,
  target: Element
): void {
  const { width, height, top, left } = target.getBoundingClientRect();

  if (!width || !height) {
    return;
  }

  iframe.style.width = `${width}px`;
  iframe.style.height = `${height}px`;
  iframe.style.top = `${top}px`;
  iframe.style.left = `${left}px`;
}

function getMedicalImageViewerConfig({
  host,
  boxAvailable,
  tenantId,
  storageId,
  studyIsShared
}: {
  host: string | undefined;
  boxAvailable: boolean;
  tenantId: string;
  storageId: string;
  studyIsShared: boolean;
}): Record<string, any> {
  if (boxAvailable) {
    return {
      dicomWeb: [
        {
          name: "DCM4CHEE",
          wadoUriRoot: `https://${host}:8443/dcm4chee-arc/aets/DCM4CHEE/wado`,
          qidoRoot: `https://${host}:8443/dcm4chee-arc/aets/DCM4CHEE/rs`,
          wadoRoot: `https://${host}:8443/dcm4chee-arc/aets/DCM4CHEE/rs`,
          qidoSupportsIncludeField: true,
          imageRendering: "wadouri",
          thumbnailRendering: "wadors",
          enableStudyLazyLoad: true,
          supportsFuzzyMatching: true
        }
      ]
    };
  } else {
    return {
      dicomWeb: [
        {
          name: "LocalStatic",
          wadoUriRoot: environment.snoots.viewer.storage.wadoUriRootFn({
            tenantId,
            storageId
          }),
          qidoRoot: environment.snoots.viewer.storage.qidoRootFn({
            tenantId,
            storageId
          }),
          wadoRoot: environment.snoots.viewer.storage.wadoRoot({
            tenantId,
            storageId
          }),
          qidoSupportsIncludeField: false,
          imageRendering: "wadors",
          thumbnailRendering: "thumbnail",
          enableStudyLazyLoad: true,
          supportsFuzzyMatching: false,
          staticWado: true
        }
      ]
    };
  }
}
